import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, PermissionService, ToastService } from '@services/index';
import { Align, Permission } from 'app/enums';
import { AgreementList, AgreementStatus, SortType } from 'app/types';
import { BehaviorSubject, debounceTime, EMPTY, finalize, map, merge, Observable, of, switchMap } from 'rxjs';
import { Filter, FilterForm } from './agreement-list.model';
import { sortStrings, sortNumeric } from '@helpers/sort-string-helper';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_ROUTES } from 'app/constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalHelper } from '@helpers/modal-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetAgreementsResponse } from '@services/api-client/responses/get-agreements-response.type';
import { AgreementRoutes } from '../../agreement.routes';
import { AgreementFilterComponent, TableType } from '../../components/filter/filter.component';
import { FilterConfig, SelectedFilter } from '../../components/filter/filter-model';
import { ChangeResponsbileModalComponent } from '../../components/change-responsible-modal/change-responsible-modal.component';
import { toDateISOString } from '@helpers/datetime-helper';
import { SendAgreementEmailModalComponent } from '../../components/send-agreement-email-modal/send-agreement-email-modal.component';

@UntilDestroy()
@Component({
    selector: 'app-agreement-list',
    templateUrl: './agreement-list.component.html',
    styleUrl: './agreement-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgreementListComponent implements OnInit {
    @ViewChild(AgreementFilterComponent) public readonly filterComponent: AgreementFilterComponent;
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly permissionService = inject(PermissionService);

    public readonly align = Align;
    public sortKey: keyof AgreementList = 'agreementNo';
    public sortAscending = true;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public showFilter = true;
    public showHeader = true;
    public isFilterEmpty = true;
    public readonly tableType = TableType.Agreements;
    public filterConfig: FilterConfig = {
        fields: ['type', 'status', 'responsible', 'endsInMonths', 'categories', 'areas', 'customers', 'counterparts']
    };

    public numOfAgreements: number = null;
    public totalValue: number = null;

    public loadingAgreementIds: string[] = [];
    public permissions = Permission;
    public userPermissions: Permission[] = this.permissionService.userPermissions;

    public includeFlags: Record<string, boolean> = {};
    private readonly sortSubject: BehaviorSubject<SortType<AgreementList>> = new BehaviorSubject<
        SortType<AgreementList>
    >({
        key: this.sortKey,
        ascending: this.sortAscending
    });
    public sort$: Observable<SortType<AgreementList>> = this.sortSubject.asObservable();
    private readonly searchSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public search$: Observable<void> = this.searchSubject.asObservable();
    private readonly deletedAgreementIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public deletedAgreementIds$: Observable<string[]> = this.deletedAgreementIdsSubject.asObservable();

    public get f() {
        return this.filterForm.controls;
    }

    private readonly getAgreementSubject = new BehaviorSubject<SelectedFilter>(null);
    public agreements$ = this.getAgreementSubject.pipe(
        //skip(1),
        debounceTime(250),
        switchMap(() => {
            const predicates = this.filterComponent.getPredicates();

            return merge(
                of(null),
                this.apiClientService.getAgreements$(predicates /*...args*/).pipe(
                    map((response: GetAgreementsResponse) => {
                        return response;
                    }),
                    switchMap((agreement: AgreementList[]) =>
                        this.deletedAgreementIds$.pipe(
                            map((deletedAgreementIds) => agreement.filter((c) => !deletedAgreementIds.includes(c.id)))
                        )
                    ),
                    switchMap((agreements: AgreementList[]) =>
                        this.search$.pipe(
                            map(() => {
                                return this.filterResponse(agreements);
                            })
                        )
                    ),
                    switchMap((agreement: AgreementList[]) => {
                        this.numOfAgreements = agreement.length;
                        this.totalValue = this.getTotalValue(agreement);
                        return of(agreement);
                    }),
                    switchMap((agreements: AgreementList[]) =>
                        this.sort$.pipe(
                            switchMap((sort) => {
                                this.sortKey = sort.key;
                                this.sortAscending = sort.ascending;
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                // Use numeric sorting for the value field
                                const sortedAgreements =
                                    sort.key === 'value'
                                        ? sortNumeric<AgreementList>(agreements, sort.key, sort.ascending)
                                        : sortStrings<AgreementList>(agreements, sort.key, sort.ascending);

                                return of(sortedAgreements);
                            })
                        )
                    )
                )
            );
        })
    );

    public ngOnInit() {
        const fb = new FormBuilder();
        this.filterForm = fb.nonNullable.group(this.defaultFilter);
        this.f.searchPhrase.valueChanges.subscribe(() =>
            this.getAgreementSubject.next(this.getCurrentAgreementRequest())
        );
        setTimeout(() => {
            this.getQueryParams();
            if (!this.isFilterEmpty) {
                this.showFilter = true;
            }
        });
    }

    private getQueryParams() {
        this.route.queryParams.subscribe((params) => {
            const statusParam = params['status'] as number;
            const responsibleIdParam = params['responsible'] as string;
            const categoryParam = params['categories'] as string;
            if (statusParam || responsibleIdParam || categoryParam) {
                this.filterComponent.clearFilter();
                if (statusParam) this.filterComponent.setFilterValue('status', statusParam);
                if (responsibleIdParam) this.filterComponent.setFilterValue('responsible', responsibleIdParam);
                if (categoryParam) this.filterComponent.setFilterValue('categories', categoryParam);
                this.getAgreementSubject.next(this.getCurrentAgreementRequest());
            }
        });
    }

    public hasWritePermission(agreement: AgreementList) {
        return this.userPermissions.includes(this.permissions.WriteAgreement) && !agreement.readOnly;
    }
    public getRowClass(agreement: AgreementList) {
        if (agreement.status == AgreementStatus.ReminderWarning) {
            if (agreement.daysLeftToNotice <= 30) {
                return 'reminderRedWarning';
            } else if (agreement.daysLeftToNotice <= 90) return 'reminderWarning';
        }
        return AgreementStatus[agreement.status].toString().toLowerCase();
    }

    public getDateForStatus(agreement: AgreementList) {
        if (
            agreement.status == AgreementStatus.Archived ||
            agreement.status == AgreementStatus.Expired ||
            agreement.status == AgreementStatus.OnGoing
        ) {
            return this.translateService.instant('FEATURES.AGREEMENTS.STATUS.' + agreement.status) as string;
        }
        if (agreement.lastDayOfNotice) return toDateISOString(new Date(agreement.lastDayOfNotice));
        return '';
    }

    public getTitle(agreement: AgreementList) {
        if (agreement.status == AgreementStatus.ReminderWarning)
            return (
                agreement.daysLeftToNotice +
                (this.translateService.instant('FEATURES.AGREEMENTS.TOOLTIPS.LIST_DAYSLEFT_TITLE') as string)
            );
        return '';
    }

    public onIncludeToggle(agreementId: string, checked: boolean) {
        this.includeFlags[agreementId] = checked;
        console.log(this.includeFlags);
    }

    public onCheckedAllChange(checked: boolean) {
        console.log(checked);
        this.agreements$.subscribe((agreements) => {
            if (agreements) {
                agreements = agreements.filter((p) => !p.readOnly);
                console.log(agreements.length);
                this.resetIncludeFlags(agreements, checked);
            }
        });
    }

    public resetIncludeFlags(agreements: AgreementList[], value: boolean = false) {
        this.includeFlags = agreements.reduce<Record<string, boolean>>((acc, agreement) => {
            acc[agreement.id] = value;
            return acc;
        }, {});
    }

    public changeResponsible() {
        const modalRef = this.modalService.open(ChangeResponsbileModalComponent);
        const modalInstance = ModalHelper.getComponentInstance<ChangeResponsbileModalComponent>(modalRef);

        modalInstance.agreementIds = this.getSelectedAgreementIds();
        modalInstance.moved$.pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.getAgreementSubject.next(this.getCurrentAgreementRequest());
            }
        });
    }

    public renegotiateAgreements() {
        const modalRef = this.modalService.open(SendAgreementEmailModalComponent);
        const modalInstance = ModalHelper.getComponentInstance<SendAgreementEmailModalComponent>(modalRef);
        modalInstance.agreementIds = this.getSelectedAgreementIds();
    }

    public confirmRemoveAgreements(event: Event) {
        event.stopPropagation();

        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);
        const selectedAgreementIds = this.getSelectedAgreementIds();

        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'TITLES.DELETE_AGREEMENT',
            bodyLanguageKey: 'TEXTS.CONFIRM_DELETE_AGREEMENT',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.changeDetectorRef.markForCheck();
                        return this.apiClientService.deleteAgreements$({ agreementIds: selectedAgreementIds });
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loadingAgreementIds = this.loadingAgreementIds.filter(
                        (id) => !selectedAgreementIds.includes(id)
                    );
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.DELETE_AGREEMENT_SUCCESS') as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.deletedAgreementIdsSubject.next([
                        ...this.deletedAgreementIdsSubject.value,
                        ...selectedAgreementIds
                    ]);
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.DELETE_AGREEMENT_ERROR') as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public hasIncludeFlags() {
        return this.getSelectedAgreementIds().length > 0;
    }
    private getSelectedAgreementIds(): string[] {
        return Object.keys(this.includeFlags).filter((id) => this.includeFlags[id]);
    }

    private getCurrentAgreementRequest() {
        return this.getAgreementSubject.value;
    }

    public async navigateToAgreement(id: string) {
        await this.router.navigate([`${APP_ROUTES.Agreement}/details/${id}`]);
    }

    public onSortChange(sortKey: keyof AgreementList, sortAscending = true) {
        this.sortSubject.next({ key: sortKey, ascending: sortAscending });
    }

    public goToCreateNewAgreement() {
        void this.router.navigate([`/${APP_ROUTES.Agreement}/${AgreementRoutes.CreateNewAgreement}`]);
    }

    public clearFilter() {
        this.filterComponent.clearFilter();
        this.filterForm.controls.searchPhrase.setValue('');
        this.getAgreementSubject.next(this.getCurrentAgreementRequest());
    }

    public onFilterChange(filter: SelectedFilter) {
        console.log('filter changed');
        this.getAgreementSubject.next(filter);
    }

    private getTotalValue(agreements: AgreementList[]): number {
        if (!agreements) return 0;
        return agreements.reduce((acc, item) => acc + (item.value || 0), 0);
    }

    private filterResponse(response: GetAgreementsResponse) {
        const searchPhrase = this.f.searchPhrase.value.toLowerCase();

        return response.filter((customer) =>
            Object.values(customer).some(
                (value) => typeof value === 'string' && value.toLowerCase().includes(searchPhrase)
            )
        );
    }
}
